import React, {useCallback, useEffect, useState} from 'react'
import {Box} from '@mui/material'
import moment from 'moment'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import PatientActivitiesCalendarNew from './PatientActivitiesCalendarNew'
import {PatientActivityRow, sortByPlannedDate} from './patientActivitiesUtils'

/**
 * New-style equivalent of src/pages/patientActivities/PatientActivities.tsx +
 * src/components/templates/ActivitiesAgenda/ActivitiesAgenda.tsx. No page title is
 * rendered here — HeaderBar derives it from the "patientActivities" nav item label.
 *
 * Same read-only calendar behavior as the legacy page: `start`/`end` (moment objects,
 * like the legacy state and like AgendaNew) default to the current month and are only
 * ever moved by the calendar's own navigation (PatientActivitiesCalendarNew), not by a
 * filter field — there is no date-range filter here, matching the legacy page exactly.
 */
export default function PatientActivitiesListNew(): JSX.Element {
  const configuration = useAppSelector(state => state.pspConfigReducer.data.patientActivitiesConfigurations)

  const [events, setEvents] = useState<PatientActivityRow[]>([])
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  // moment objects — getPatientsActivities only needs `.toISOString()`, which both
  // moment and Date provide (see the same `useState<any>` pattern in AgendaNew.tsx).
  const [start, setStart] = useState<any>(moment().startOf('month'))
  const [end, setEnd] = useState<any>(moment().endOf('month'))

  const fetchData = useCallback(() => {
    apiClient.getPatientsActivities(activeFilters, start, end, setStatus)
      .then((data: any) => {
        if (Array.isArray(data)) setEvents(data)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [activeFilters, start, end])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
  }

  const filterConfig: FilterItem[] = [
    {id: 'beneficiaryLastName', label: 'Cognome', type: 'text'},
    {id: 'beneficiaryFirstName', label: 'Nome', type: 'text'},
    {id: 'drugId', label: 'Terapia (Farmaco)', type: 'select', options: configuration?.drugs ?? []},
    // Free-text field, matching the legacy page exactly (src/pages/patientActivities/
    // PatientActivities.tsx uses type: 'string' here too) — not a select, even though
    // the PSP config also exposes a structured activitiesTypes catalog.
    {id: 'activityType', label: 'Tipo di attività', type: 'text'},
  ]

  const sortedEvents = sortByPlannedDate(events)

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {status === STATUS.REJECTED && (
        <InlineMessage
          severity="error"
          onClose={() => setStatus(STATUS.FULFILLED)}
          sx={{mb: 2}}
        >
          {'Errore nel caricare le attività dei pazienti.'}
        </InlineMessage>
      )}

      <PatientActivitiesCalendarNew
        events={sortedEvents}
        startDate={start}
        setStartDate={setStart}
        endDate={end}
        setEndDate={setEnd}
      />
    </Box>
  )
}
